import React, { useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import "moment/locale/es";
import { tokens } from "../../theme";
import { LoaderSpinner } from "../../components/spinner/loader";
import { MetricCard, Num, OrderStatusChip, QuickAction, SectionCard } from "../../components/ui";
import {
  IconAlert,
  IconBilling,
  IconCheck,
  IconChevronRight,
  IconClients,
  IconClock,
  IconMoneyIn,
  IconMoneyOut,
  IconPlus,
  IconWallet,
} from "../../components/ui/icons";
import { RANGES, useAdminMetrics } from "../../hooks/useDashboardMetrics";
import { getOrderStatusStyle } from "../../constants/statusStyles";
import { selectableOrderStatus } from "../../constants/orderStatus";
import { formatAmountShort, formatCompactCurrency } from "../../utils/formatters";
import { ROUTES } from "../../components/layout/navConfig";

moment.locale("es");

const TONE_COLOR = {
  danger: tokens.brandDark,
  // Con el ámbar literal del tema claro, este texto quedaba en 2,9:1 sobre el
  // fondo oscuro. La variable de estado ya trae el tono de cada tema.
  warn: "var(--sup-state-warn-ink)",
  muted: tokens.ink3,
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [range, setRange] = useState("mes");
  const { data, loading, error, reload } = useAdminMetrics(range);

  if (loading && !data) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 240 }}>
        <LoaderSpinner />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, paddingY: 6 }}>
        <Typography sx={{ color: tokens.ink2 }}>{error}</Typography>
        <Button variant="outlined" onClick={reload}>
          Reintentar
        </Button>
      </Box>
    );
  }

  const { toInvoice, receivable, cash, checks, attention, statusCounts, statusTotal } = data;

  const cashDetail = cash.boxes
    .slice(0, 2)
    .map((box) => `${box.description || "Caja"} ${formatAmountShort(box.balance)}`)
    .join(" · ");

  const nextCheck = checks.upcoming[0];
  const nextCheckDays = nextCheck ? nextCheck.dueDate.diff(moment().startOf("day"), "days") : null;

  const shortcuts = [
    {
      label: "Nueva orden",
      icon: IconPlus,
      color: tokens.brandDark,
      background: tokens.brandTint,
      path: ROUTES.newOrder,
    },
    {
      label: "Registrar pago",
      icon: IconBilling,
      color: "#45499b",
      background: "#eceef8",
      path: ROUTES.billing,
    },
    {
      label: "Ingresar dinero",
      icon: IconMoneyIn,
      color: "#3f6b1f",
      background: "#eef4e4",
      path: ROUTES.cashAdd,
    },
    {
      label: "Retirar dinero",
      icon: IconMoneyOut,
      color: "#8a5a12",
      background: "#fbf1e2",
      path: ROUTES.cashExtraction,
    },
    {
      label: "Cuenta corriente",
      icon: IconClients,
      color: "#14675e",
      background: "#e4f2f0",
      path: ROUTES.currentAccounts,
    },
    {
      label: "Reporte de cheques",
      icon: IconCheck,
      color: tokens.ink2,
      background: tokens.borderSoft,
      path: ROUTES.checkReports,
    },
  ];

  const openOrder = (order) =>
    navigate("/order-details-form", { state: { order, source: "dashboard" } });

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "16px", width: "100%" }}>
      {/* Métricas: el estado del negocio ahora mismo, cada una con su acción. */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", lg: "repeat(4, minmax(0, 1fr))" },
          gap: { xs: "10px", md: "16px" },
        }}
      >
        <MetricCard
          label="Listas para facturar"
          icon={IconBilling}
          iconColor="#45499b"
          iconBackground="#eceef8"
          value={formatCompactCurrency(toInvoice.amount)}
          hint={
            toInvoice.count === 0
              ? "Nada pendiente de facturar"
              : `${toInvoice.count} ${toInvoice.count === 1 ? "orden" : "órdenes"}${
                  toInvoice.oldestDays !== null ? ` · la más vieja hace ${toInvoice.oldestDays} d` : ""
                }`
          }
          footer="Facturar ahora"
          onClick={() => navigate(ROUTES.billing)}
        />

        <MetricCard
          label="Cobranza pendiente"
          icon={IconClock}
          iconColor="#8a5a12"
          iconBackground="#fbf1e2"
          value={formatCompactCurrency(receivable.amount)}
          hint={
            receivable.overdue > 0
              ? `${receivable.count} facturas · ${formatCompactCurrency(receivable.overdue)} vencido +30 d`
              : `${receivable.count} facturas sin vencer`
          }
          hintColor={receivable.overdue > 0 ? tokens.brandDark : undefined}
          footer="Ver cuentas corrientes"
          onClick={() => navigate(ROUTES.currentAccounts)}
        />

        <MetricCard
          label="Saldo en caja"
          icon={IconWallet}
          iconColor="#3f6b1f"
          iconBackground="#eef4e4"
          value={formatCompactCurrency(cash.total)}
          hint={cashDetail || "Sin cajas cargadas"}
          footer="Ver movimientos"
          onClick={() => navigate(ROUTES.cashMovements)}
        />

        <MetricCard
          label="Cheques en cartera"
          icon={IconCheck}
          iconColor="#14675e"
          iconBackground="#e4f2f0"
          value={formatCompactCurrency(checks.amount)}
          hint={
            nextCheck
              ? `${checks.count} cheques · el próximo vence ${
                  nextCheckDays <= 0 ? "hoy" : `en ${nextCheckDays} d`
                }`
              : `${checks.count} cheques pendientes`
          }
          footer={
            checks.rejectedCount > 0
              ? `${checks.rejectedCount} rechazado${checks.rejectedCount === 1 ? "" : "s"} · ${formatCompactCurrency(checks.rejectedAmount)}`
              : "Ver reporte"
          }
          footerIcon={checks.rejectedCount > 0 ? IconAlert : undefined}
          onClick={() => navigate(ROUTES.checkReports)}
        />
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "minmax(0, 1fr)", lg: "minmax(0, 1fr) 352px" },
          gap: "16px",
          alignItems: "start",
        }}
      >
        {/* Columna principal */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: "16px", minWidth: 0 }}>
          <SectionCard>
            <Box sx={{ padding: "16px 18px 18px", display: "flex", flexDirection: "column", gap: "14px" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: "2px", flexGrow: 1 }}>
                  <Typography sx={{ fontSize: "14px", fontWeight: 600 }}>Órdenes creadas</Typography>
                  <Typography sx={{ fontSize: "12px", color: tokens.ink3 }}>
                    Por estado actual · <Num>{statusTotal}</Num> en el período
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: "2px", padding: "3px", backgroundColor: tokens.borderSoft, borderRadius: "9px" }}>
                  {RANGES.map((r) => {
                    const on = r.id === range;
                    return (
                      <Box
                        component="button"
                        type="button"
                        key={r.id}
                        onClick={() => setRange(r.id)}
                        sx={{
                          border: "none",
                          borderRadius: "7px",
                          padding: "5px 11px",
                          minHeight: { xs: 36, md: 28 },
                          fontSize: "12.5px",
                          fontFamily: "inherit",
                          cursor: "pointer",
                          fontWeight: on ? 500 : 400,
                          backgroundColor: on ? tokens.surface : "transparent",
                          color: on ? tokens.ink : "#78736f",
                          boxShadow: on ? tokens.shadowSm : "none",
                        }}
                      >
                        {r.label}
                      </Box>
                    );
                  })}
                </Box>
              </Box>

              <Box sx={{ display: "flex", height: 10, borderRadius: 999, overflow: "hidden", backgroundColor: tokens.borderSoft, gap: "2px" }}>
                {selectableOrderStatus.map((s) => {
                  const count = statusCounts[s.value] || 0;
                  if (!count) return null;
                  const pct = statusTotal ? (count / statusTotal) * 100 : 0;
                  return (
                    <Box
                      key={s.value}
                      title={`${s.label}: ${count}`}
                      sx={{ width: `${pct}%`, backgroundColor: getOrderStatusStyle(s.value).dot }}
                    />
                  );
                })}
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: "repeat(4, minmax(0, 1fr))" },
                  gap: "9px 18px",
                }}
              >
                {selectableOrderStatus.map((s) => (
                  <Box key={s.value} sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: 999,
                        flexShrink: 0,
                        backgroundColor: getOrderStatusStyle(s.value).dot,
                      }}
                    />
                    <Typography sx={{ fontSize: "12.5px", color: tokens.ink2, flexGrow: 1 }}>
                      {s.label}
                    </Typography>
                    <Num sx={{ fontSize: "12.5px", fontWeight: 600 }}>
                      {statusCounts[s.value] || 0}
                    </Num>
                  </Box>
                ))}
              </Box>
            </Box>
          </SectionCard>

          <SectionCard
            title="Requieren tu atención"
            subtitle="Órdenes frenadas por algo que falta"
            action={attention.length ? `${attention.length} en total` : undefined}
          >
            {attention.length === 0 ? (
              <Box sx={{ padding: "28px 18px", textAlign: "center" }}>
                <Typography sx={{ fontSize: "13.5px", color: tokens.ink3 }}>
                  No hay órdenes frenadas. Todo al día.
                </Typography>
              </Box>
            ) : (
              attention.slice(0, 8).map(({ order, reason }, index, list) => (
                <Box
                  key={order.orderId}
                  onClick={() => openOrder(order)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "11px",
                    padding: { xs: "13px 14px", md: "11px 18px" },
                    minHeight: { xs: 68, md: 56 },
                    cursor: "pointer",
                    borderBottom: index === list.slice(0, 8).length - 1 ? "none" : "1px solid #f5f3f1",
                    "&:hover": { backgroundColor: tokens.surface2 },
                  }}
                >
                  <Box
                    sx={{
                      width: "3px",
                      alignSelf: "stretch",
                      borderRadius: "2px",
                      flexShrink: 0,
                      backgroundColor: getOrderStatusStyle(order.status).dot,
                      display: { xs: "block", md: "none" },
                    }}
                  />
                  <Box sx={{ display: "flex", flexDirection: "column", gap: "3px", flexGrow: 1, minWidth: 0 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: "8px", minWidth: 0 }}>
                      <Num sx={{ fontSize: "12.5px", fontWeight: 600, color: tokens.ink2 }}>
                        #{order.orderId}
                      </Num>
                      <Typography
                        sx={{
                          fontSize: "13.5px",
                          fontWeight: 500,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {order.client?.company || "Sin cliente"}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <Box sx={{ display: { xs: "none", md: "block" } }}>
                        <OrderStatusChip status={order.status} size="sm" />
                      </Box>
                      <Typography sx={{ fontSize: "12px", color: TONE_COLOR[reason.tone] }}>
                        {reason.text}
                      </Typography>
                    </Box>
                  </Box>
                  <Num sx={{ fontSize: "13px", fontWeight: 600, flexShrink: 0 }}>
                    {formatAmountShort(
                      (parseFloat(order.subTotal) || 0) +
                        (parseFloat(order.iva105) || 0) +
                        (parseFloat(order.iva210) || 0)
                    )}
                  </Num>
                  <Box sx={{ color: tokens.ink4, display: "flex", flexShrink: 0 }}>
                    <IconChevronRight size={16} />
                  </Box>
                </Box>
              ))
            )}
          </SectionCard>
        </Box>

        {/* Columna lateral */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: "16px", minWidth: 0 }}>
          <SectionCard title="Accesos directos" padded={false}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", sm: "repeat(3, minmax(0, 1fr))", lg: "repeat(2, minmax(0, 1fr))" },
                gap: "8px",
                padding: "14px 16px 16px",
              }}
            >
              {shortcuts.map((shortcut) => (
                <QuickAction
                  key={shortcut.label}
                  icon={shortcut.icon}
                  label={shortcut.label}
                  color={shortcut.color}
                  background={shortcut.background}
                  onClick={() => {
                    localStorage.removeItem("signatureData");
                    navigate(shortcut.path);
                  }}
                />
              ))}
            </Box>
          </SectionCard>

          <SectionCard
            title="Cheques por vencer"
            subtitle="Pendientes de cobro, del más próximo al más lejano"
            action="Reporte"
            onAction={() => navigate(ROUTES.checkReports)}
          >
            {checks.upcoming.length === 0 ? (
              <Box sx={{ padding: "24px 16px", textAlign: "center" }}>
                <Typography sx={{ fontSize: "13px", color: tokens.ink3 }}>
                  No hay cheques pendientes de cobro.
                </Typography>
              </Box>
            ) : (
              checks.upcoming.slice(0, 5).map((check, index, list) => {
                const days = check.dueDate.diff(moment().startOf("day"), "days");
                const isSoon = days <= 7;
                return (
                  <Box
                    key={`${check.checkNumber}-${index}`}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      padding: "11px 16px",
                      minHeight: 56,
                      borderBottom: index === list.length - 1 ? "none" : "1px solid #f5f3f1",
                    }}
                  >
                    <Box
                      sx={{
                        width: 34,
                        height: 34,
                        borderRadius: "8px",
                        flexShrink: 0,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        lineHeight: 1.05,
                        backgroundColor: isSoon ? tokens.brandTint : tokens.borderSoft,
                        color: isSoon ? tokens.brandDark : tokens.ink2,
                      }}
                    >
                      <Num sx={{ fontSize: "13px", fontWeight: 600 }}>{check.dueDate.format("DD")}</Num>
                      <Box component="span" sx={{ fontSize: "9px", fontWeight: 500, textTransform: "uppercase" }}>
                        {check.dueDate.format("MMM")}
                      </Box>
                    </Box>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: "2px", flexGrow: 1, minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontSize: "13px",
                          fontWeight: 500,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {check.checkBank || "Banco sin dato"} · {check.checkNumber || "s/n"}
                      </Typography>
                      <Typography sx={{ fontSize: "11.5px", color: tokens.ink3 }}>
                        {check.checkIssuerName || "Sin librador"} ·{" "}
                        {days <= 0 ? "vencido" : `en ${days} ${days === 1 ? "día" : "días"}`}
                      </Typography>
                    </Box>
                    <Num sx={{ fontSize: "13px", fontWeight: 600, flexShrink: 0 }}>
                      {formatAmountShort(check.checkAmount)}
                    </Num>
                  </Box>
                );
              })
            )}
          </SectionCard>
        </Box>
      </Box>
    </Box>
  );
};

export default Dashboard;
